import { fefoOrder, isExpired } from '@felix/domain';
import type { SortState } from '../materials/SortHeader.js';
import type {
  ProvisionCategory,
  ProvisionItemQuery,
  ProvisionLot,
  ProvisionSort,
  ProvisionStatus,
} from './types.js';

/**
 * Kumanya ekranının saf hesapları (ADR 0099): kategori ağacı, FEFO önizlemesi (gemi sunucusunun
 * kuralıyla aynı: `@felix/domain` `fefoOrder`), liste sorgusu.
 */

export interface CategoryNode {
  /** Ana kategori kodu (`PRV-01`); kodsuz kategoride ana kategori adı. */
  key: string;
  categoryCode: string | null;
  mainCategory: string;
  items: number;
  onBoard: number;
  children: ProvisionCategory[];
}

/** Ana kategoriler (kod ya da ad) ve alt kategorileri; sunucunun sırası korunur. */
export function categoryTree(categories: readonly ProvisionCategory[]): CategoryNode[] {
  const nodes = new Map<string, CategoryNode>();
  for (const category of categories) {
    const key = category.categoryCode ?? category.mainCategory;
    const node = nodes.get(key) ?? {
      key,
      categoryCode: category.categoryCode,
      mainCategory: category.mainCategory,
      items: 0,
      onBoard: 0,
      children: [],
    };
    node.items += category.items;
    node.onBoard += category.onBoard;
    node.children.push(category);
    nodes.set(key, node);
  }
  return [...nodes.values()];
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/** Kalemin lotsuz bakiyesi (lot öncesi eski hareketler): eldeki eksi lot bakiyeleri. */
export function unassignedBalance(onHand: number, lots: readonly ProvisionLot[]): number {
  return Math.max(0, round(onHand - lots.reduce((sum, lot) => sum + lot.balance, 0)));
}

/** Tüketime çıkabilecek lotlar, FEFO sırasında (süresi geçmiş ve boş lot yok). */
export function issuableLots(lots: readonly ProvisionLot[], today: string): ProvisionLot[] {
  const byId = new Map(lots.map((lot) => [lot.id, lot]));
  return fefoOrder(lots, today).flatMap((lot) => byId.get(lot.id) ?? []);
}

/**
 * Lot verilmeyen çıkışın dağıtımı (ADR 0099 §7): sunucu aynı sırayla böler. Yetmeyen miktar
 * `shortage`'da (sunucu `NEGATIVE_STOCK` verir); hata atmaz, formda gösterilir.
 */
export function fefoPreview(
  lots: readonly ProvisionLot[],
  quantity: number,
  today: string,
  unassigned: number,
): { allocations: { lotId: string | null; quantity: number }[]; shortage: number } {
  let left = round(quantity > 0 ? quantity : 0);
  const allocations: { lotId: string | null; quantity: number }[] = [];
  for (const lot of fefoOrder(lots, today)) {
    if (left <= 0) break;
    const take = Math.min(left, lot.balance);
    allocations.push({ lotId: lot.id, quantity: round(take) });
    left = round(left - take);
  }
  if (left > 0 && unassigned > 0) {
    const take = Math.min(left, unassigned);
    allocations.push({ lotId: null, quantity: round(take) });
    left = round(left - take);
  }
  return { allocations, shortage: left };
}

/**
 * Seçilen lot FEFO'dan sapıyor mu: süresi geçmemiş ve son kullanması daha erken bir lot var.
 * Aynı tarihli lotlar arasında seçim sapma değildir (sunucunun `checkLotIssue` kuralı).
 */
export function fefoDeviation(
  lots: readonly ProvisionLot[],
  lotId: string,
  today: string,
): boolean {
  const lot = lots.find((l) => l.id === lotId);
  const first = fefoOrder(lots, today)[0];
  if (!lot || !first || first.id === lot.id) return false;
  return (first.expiryDate ?? '9999-12-31') < (lot.expiryDate ?? '9999-12-31');
}

/** Lotun süresi geçmiş mi (gemi yerel günü; son kullanma günü kullanılabilir). */
export function lotExpired(lot: Pick<ProvisionLot, 'expiryDate'>, today: string): boolean {
  return isExpired(lot.expiryDate, today);
}

export interface ItemFilter {
  q: string;
  storageType: string;
  allergen: string;
  status: '' | ProvisionStatus;
  expiry: '' | 'expiring' | 'expired';
  stockOnly: boolean;
}

export const EMPTY_FILTER: ItemFilter = {
  q: '',
  storageType: '',
  allergen: '',
  status: '',
  expiry: '',
  stockOnly: true,
};

export const DEFAULT_PROVISION_SORT: SortState<ProvisionSort> = {
  column: 'category',
  direction: 'asc',
};

/** Liste sorgusu: boş süzgeç yazılmaz, varsayılan sıra gönderilmez. */
export function itemsQuery(
  filter: ItemFilter,
  category: { categoryCode: string | null; categoryId: string | null },
  sort: SortState<ProvisionSort>,
  page: number,
  pageSize: number,
): ProvisionItemQuery {
  const q = filter.q.trim();
  const isDefault =
    sort.column === DEFAULT_PROVISION_SORT.column &&
    sort.direction === DEFAULT_PROVISION_SORT.direction;
  return {
    ...(q ? { q } : {}),
    ...(filter.storageType ? { storageType: filter.storageType } : {}),
    ...(filter.allergen ? { allergen: filter.allergen } : {}),
    ...(filter.status ? { status: filter.status } : {}),
    ...(filter.expiry ? { expiry: filter.expiry } : {}),
    ...(filter.stockOnly ? { stockOnly: true } : {}),
    ...(category.categoryId
      ? { categoryId: category.categoryId }
      : category.categoryCode
        ? { categoryCode: category.categoryCode }
        : {}),
    page,
    pageSize,
    ...(isDefault ? {} : { sort: sort.column, direction: sort.direction }),
  };
}

export function percent(part: number, total: number): number {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}
